import React, { FC, useReducer } from 'react';
import { Form as FormikForm, Formik } from 'formik';
import { cloneDeep, compact, pick, union } from 'lodash';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  ContentApproval,
  ContentFragment,
  ContentInput,
  GroupIdApprovalInput,
  ManualPostMutation,
  Network,
  ProcessContentV2Mutation,
  useManualPostMutation,
  useProcessContentV2Mutation,
  useUpdateContentApprovalsMutation,
} from 'api';
import { useFollowed, useIntegrations, useNotify, useProtectedClient, useReleases } from 'hooks';
import { EMPTY_SLATE_EDITOR } from 'app-constants';
import { getPostableGroupsToApprove, hasPhoto, hasUrl, hasVideo } from 'features/content/helpers';
import { BlockListProvider } from 'providers';
import * as paths from 'paths';

import { getSafeBody, formatDisclosures } from '../helpers';
import { IPostUIState, PostOption } from '../types';
import { initialPostUIState, postContextReducer, PostUIContext } from '../PostContext';

import Post from './Post';
import { PostSchema } from '../validations';

interface Props {
  content: ContentFragment;
}

const PostEdit: FC<Props> = ({ content }) => {
  const notify = useNotify();
  const client = useProtectedClient();
  const { postable } = useFollowed();
  const integrations = useIntegrations();
  const releases = useReleases();
  const location = useLocation();
  const navigate = useNavigate();

  const hasBlocklist = releases.includes('blocklist');
  const hasLinkedInMentions = releases.includes('linkedinCompanyMentions');
  const hasSlack = integrations.some(integration => integration.provider === Network.Slack);
  const hasMSTeams = integrations.some(integration => integration.provider === Network.Msteams);

  const [state, dispatch] = useReducer(postContextReducer, getInitialPostUIState());

  const { mutate: processContent } = useManualPostMutation({
    onError: notify.mutationError,
    onSuccess: handleCompleted,
  });

  const { mutate: processContentV2 } = useProcessContentV2Mutation({
    onError: notify.mutationError,
    onSuccess: handleCompletedV2,
  });

  const { mutate: updateContentApprovals } = useUpdateContentApprovalsMutation({
    onError: notify.mutationError,
    onSuccess: () => {
      setTimeout(() => {
        location.key ? navigate(-1) : navigate(paths.index);
      }, 1000);
    },
  });

  function getInitialPostOption(): PostOption {
    if (hasUrl(content)) {
      if (hasVideo(content)) {
        return PostOption.ImageVideo;
      }
      return PostOption.Link;
    }

    if (hasPhoto(content) || hasVideo(content)) {
      return PostOption.ImageVideo;
    }

    return PostOption.Text;
  }

  function getInitialPostUIState(): IPostUIState {
    const { group_id_approvals, content_object } = content;

    const selectedPhotoIndexes = content_object.photos?.map((_, idx) => idx) ?? [];

    return {
      ...initialPostUIState,
      option: getInitialPostOption(),
      schedule: !!content.schedule?.publish || !!content?.schedule?.expire,
      pin: content.is_pinned || !!content?.pinned_to_groups?.length,
      commentary: !!content.commentary?.length,
      disclosures: !!content.disclosures?.length,
      tracking: !!content.url_param_values?.length,
      requiresApproval:
        group_id_approvals?.length && group_id_approvals[0].approval === 'submitted',
      selectedPhotoIndexes,
      repost: content.primary_group_id && content.group_ids.length > 1,
    };
  }

  function createShareCopies() {
    const emptyTwitter = { network: Network.Twitter, commentary: '' };
    const emptyLinkedIn = {
      network: Network.Linkedin,
      commentary: '',
      ...(hasLinkedInMentions ? { mentions: [] } : {}),
    };
    const emptyFacebook = { network: Network.Facebook, commentary: '' };
    const emptyInstagram = { network: Network.Instagram, commentary: '' };

    if (!content.commentary) {
      return [emptyTwitter, emptyLinkedIn, emptyFacebook, emptyInstagram];
    }

    function getCommentary(network: Network) {
      const selectedCommentaries = content.commentary.filter(
        commentary => commentary.network === network
      );
      if (selectedCommentaries.length) {
        return selectedCommentaries;
      }

      switch (network) {
        case Network.Twitter:
          return [emptyTwitter];
        case Network.Linkedin:
          return [emptyLinkedIn];
        case Network.Facebook:
          return [emptyFacebook];
        case Network.Instagram:
          return [emptyInstagram];
        default:
          return [];
      }
    }

    return union(
      getCommentary(Network.Twitter),
      getCommentary(Network.Linkedin),
      getCommentary(Network.Facebook),
      getCommentary(Network.Instagram)
    );
  }

  function createDisclosures() {
    const emptyTwitter = { network: Network.Twitter, disclosure_id: '' };
    const emptyLinkedIn = { network: Network.Linkedin, disclosure_id: '' };
    const emptyFacebook = { network: Network.Facebook, disclosure_id: '' };
    const emptyInstagram = { network: Network.Instagram, disclosure_id: '' };

    if (!content.disclosures) {
      return [emptyTwitter, emptyLinkedIn, emptyFacebook, emptyInstagram];
    }

    function getDisclosures(network: Network) {
      return content.disclosures.find(disclosure => disclosure.network === network);
    }

    return [
      getDisclosures(Network.Twitter) || emptyTwitter,
      getDisclosures(Network.Linkedin) || emptyLinkedIn,
      getDisclosures(Network.Facebook) || emptyFacebook,
      getDisclosures(Network.Instagram) || emptyInstagram,
    ];
  }

  function getValues() {
    const contentInput = pick<ContentInput>(
      content as unknown as ContentInput,
      'group_ids',
      'content_id',
      'content_source_id',
      'is_engageable',
      'is_important',
      'is_shareable',
      'is_pinned',
      'is_branded',
      'pinned_end_at',
      'lock_commentary',
      'is_important_message',
      'url_param_values',
      'pinned_to_groups',
      'primary_group_id',
      'commentary',
      'disclosures',
      'schedule',
      'groups',
      'group_id_approvals',
      'content_object.post_type',
      'content_object.body',
      'content_object.url',
      'content_object.title',
      'content_object.photos',
      'content_object.media',
      'content_object.url_metadata',
      'publish_at',
      'slack_channels',
      'content_tags',
      'ms_teams_channels'
    );

    contentInput.content_object.photos = content.content_object.photos || [];
    contentInput.primary_group_id = content.primary_group_id || content.group_ids[0];
    contentInput.content_tags = content.content_tags || [];
    contentInput.content_object.body = content.content_object.body || EMPTY_SLATE_EDITOR;
    contentInput.content_object.url = content.content_object.url || '';
    contentInput.content_object.title = content.content_object.title || '';
    contentInput.pinned_to_groups = content.pinned_to_groups || [];
    contentInput.content_object.url_metadata_override = {
      title: contentInput?.content_object?.url_metadata?.title,
      description: contentInput?.content_object?.url_metadata?.description,
    };

    contentInput.is_engageable = content.is_engageable || false;
    contentInput.is_important_message = content.is_important_message || '';
    contentInput.url_param_values = content.url_param_values || [];
    contentInput.schedule = content.schedule || { publish: null, expire: null };
    contentInput.commentary = createShareCopies();
    contentInput.disclosures = createDisclosures();

    return contentInput as ContentInput;
  }

  function handleCompleted(manualPostResponse: ManualPostMutation) {
    const processContentResult = manualPostResponse.processContent;
    const groupsToApprove = getPostableGroupsToApprove(processContentResult, postable.list);

    notify.info({ i18n: { i18nKey: 'posting:modified' } });

    if (
      !groupsToApprove.length ||
      processContentResult.schedule.publish ||
      processContentResult.schedule.expire
    ) {
      setTimeout(() => {
        location.key ? navigate(-1) : navigate(paths.index);
      }, 1000);
      return;
    }

    const groupIdApprovals: GroupIdApprovalInput[] = groupsToApprove.map(submittedGroup => ({
      approval: ContentApproval.Approved,
      group_id: submittedGroup.id,
    }));

    updateContentApprovals({
      content_id: processContentResult.content_id,
      group_id_approvals: groupIdApprovals,
    });
  }

  function handleCompletedV2(manualPostResponse: ProcessContentV2Mutation) {
    const processContentResult = manualPostResponse.processContentV2?.content;
    const groupsToApprove = getPostableGroupsToApprove(processContentResult, postable.list);
    notify.info({ i18n: { i18nKey: 'posting:modified' } });

    if (
      !groupsToApprove.length ||
      processContentResult.schedule.publish ||
      processContentResult.schedule.expire
    ) {
      setTimeout(() => {
        location.key ? navigate(-1) : navigate(paths.index);
      }, 1000);
      return;
    }

    const groupIdApprovals: GroupIdApprovalInput[] = groupsToApprove.map(submittedGroup => ({
      approval: ContentApproval.Approved,
      group_id: submittedGroup.id,
    }));

    updateContentApprovals({
      content_id: processContentResult.content_id,
      group_id_approvals: groupIdApprovals,
    });
  }

  function onSubmit(contentInput: ContentInput) {
    const contentToSubmit: ContentInput = cloneDeep(contentInput);

    contentToSubmit.content_object.photos = contentInput.content_object.photos.filter((_, idx) =>
      state.selectedPhotoIndexes.includes(idx)
    );

    contentToSubmit.group_ids = compact(contentToSubmit.group_ids);
    contentToSubmit.content_object.body = getSafeBody(contentToSubmit);
    contentToSubmit.commentary = contentToSubmit.commentary.filter(c => !!c.commentary);
    contentToSubmit.disclosures = formatDisclosures(contentToSubmit);

    if (!state.contentTagging) {
      contentToSubmit.content_tags = [];
    }

    if (!state.slack) {
      contentToSubmit.slack_channels = [];

      if (!hasSlack) {
        delete contentToSubmit.slack_channels;
      }
    }

    if (!state.msteams) {
      contentToSubmit.ms_teams_channels = [];

      if (!hasMSTeams) {
        delete contentToSubmit.ms_teams_channels;
      }
    }

    delete contentToSubmit.content_object.url_metadata;

    if (contentInput?.content_object?.title) {
      contentToSubmit.content_object.url_metadata_override = {
        title: contentInput?.content_object?.title,
      };
    }

    // Due to the wonky approval process we need `group_id_approvals` and `groups`
    // while editing a post. We grab the values and then delete on the way out the door.
    // These attributes are only needed for dynamic save button text

    // @ts-ignore
    delete contentToSubmit.group_id_approvals;

    // @ts-ignore
    delete contentToSubmit.groups;

    if (hasBlocklist) {
      contentToSubmit.commentary = contentToSubmit.commentary.map(share => ({
        commentary: share.commentary,
        network: share.network,
        ...(hasLinkedInMentions ? { mentions: share.mentions || [] } : {}),
      }));
      return processContentV2({ content: contentToSubmit });
    }

    return processContent({ content: contentToSubmit });
  }

  return (
    <PostUIContext.Provider value={{ state, dispatch }}>
      <BlockListProvider client_id={client.id}>
        <Formik
          validateOnChange={true}
          onSubmit={onSubmit}
          initialValues={getValues()}
          enableReinitialize
          validationSchema={PostSchema}
          options={{ uiState: state }}
        >
          <FormikForm>
            <Post />
          </FormikForm>
        </Formik>
      </BlockListProvider>
    </PostUIContext.Provider>
  );
};

export default PostEdit;
